/* eslint-disable */
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IdentityContext } from '../../context/userInformation/IdentityContextProvider';
import { CreateCustomerStg } from '../../api/user';
import { getBase64Size, compressBase64Image, uploadDocuments } from '../helperFunction/HelperFunction';
import { StgIdentityRecord } from '../../interface/CustomerInformationInterface';

interface UseNAATResultHandlersParams {
  setScreenLoader: React.Dispatch<React.SetStateAction<boolean>>;
  setNaatIDTrialsCount: React.Dispatch<React.SetStateAction<number>>;
  setTranId: React.Dispatch<React.SetStateAction<string>>;
  hasTriggered: React.MutableRefObject<boolean>;
  latestIdentityRecord: StgIdentityRecord | undefined;
  canEnableCountryCode: { paramValue?: string } | undefined;
  sessionStorageValues: any;
  // Shared failure handler (defined in IdentityTab) — called when the selfie
  // result has no usable images so trial-count/retry logic stays centralized.
  onSelfieFailure: (reason: string) => void;
}

// Encapsulates the two NAAT SDK "result" handlers used by captureID's
// onCaptureIdComplete / onSelfieVerificationComplete callbacks:
//  - readSdkResults: populates the identity form from the capture-ID OCR result.
//  - readSelfieVerificationData: uploads the selfie image and submits the
//    staging identity record to the manage/ProfileStg API.
export const useNAATResultHandlers = ({
  setScreenLoader,
  setNaatIDTrialsCount,
  setTranId,
  hasTriggered,
  latestIdentityRecord,
  canEnableCountryCode,
  sessionStorageValues,
  onSelfieFailure,
}: UseNAATResultHandlersParams) => {
  const { t } = useTranslation();
  const history = useHistory();
  const context = useContext(IdentityContext);
  const {
    identityInformation,
    setIdentityInformation,
    stgCustomerProfileId,
    getStgCustomerRes,
    setIdentityFrontDocument,
    setIdentityBackDocument,
    setIDValidationEnable,
    setLiveLinessCheckEnable,
    setLiveLinessCompleted,
    setIdValidationCompleted,
  } = context!;

  const readSdkResults = (sdkResult: any, apiResult?: any) => {
    try {
      setScreenLoader(true)
      setIDValidationEnable(false)
      setLiveLinessCheckEnable(false)
      console.log('sdk result', sdkResult);
      const ocrData = sdkResult?.capture_result;
      console.log('ocrData', ocrData);
      if (ocrData) {
        const gender = ocrData.gender && ocrData.gender === 'F' ? 'FMALE' : ocrData.gender === 'M' ? 'MALE' : 'OTH';
        setIdentityInformation((prev: any) => ({
          ...prev,
          firstName: ocrData?.given_names || '',
          paternalLastName: ocrData?.paternal_last_name || '',
          maternalLastName: ocrData?.maternal_last_name || '',
          curp: ocrData?.curp_number || '',
          ineVoterid: ocrData?.voter_key || '',
          gender: gender || '',
          dateOfBirth: ocrData?.dob || '',
          rfc: ocrData?.rfc || '',
        }))
      }
      setTranId(apiResult?.transaction_id || '')
      setIdentityFrontDocument(ocrData?.images?.front || '');
      setIdentityBackDocument(ocrData?.images?.back || '');

      if (ocrData?.images?.front && ocrData?.images?.back) {
        setIdValidationCompleted(true)
      }
      setScreenLoader(false)
      if (identityInformation) {
        setIDValidationEnable(false)
      }
    }
    catch (e: any) {
      console.log('Error in readSdk Function', { e });
      hasTriggered.current = false;
      setNaatIDTrialsCount((count) => count + 1);
      setScreenLoader(false);
      setIDValidationEnable(true);
      setIdValidationCompleted(false);
    }
  }

  const readSelfieVerificationData = async (result: any) => {
    setScreenLoader(true)
    try {
      const faceBase64 = result?.apiResult?.images?.face;
      const stgCusProfId = String(stgCustomerProfileId);
      console.log('stgCusProfId ', stgCusProfId, faceBase64)
      const documentsToUpload = [];
      if (result?.apiResult?.images) {
        let documentFrontData;
        let base64;
        if (typeof faceBase64 === 'string') {
          console.log(' inside  the NAAT document');
          const sizeInBytes = await getBase64Size(faceBase64);
          console.log('size in bytes ', sizeInBytes);
          const maxSize = 3 * 1024 * 1024; // size of 3 MB
          console.log('maxSize', maxSize);
          if (sizeInBytes > maxSize) {
            console.log("inside the function ");
            base64 = await compressBase64Image(faceBase64);
            documentFrontData = base64;
          }
          else {
            documentFrontData = faceBase64;
          }
        }
        if (documentFrontData) {
          documentsToUpload.push({
            documentType: 'SELFIE',
            file: `dataimage/jpegbase64${documentFrontData}`,
          });


          await uploadDocuments(stgCusProfId, documentsToUpload, null);

          // If no errors, proceed to the next step
          const idDocType: string | null = sessionStorageValues?.idDocType;
          const stgCustomerPayload = {
            stgCustomerProfileId: Number(stgCustomerProfileId) ? stgCustomerProfileId : undefined,
            action: Number(stgCustomerProfileId) ? "UPD" : "CRT",
            firstName: identityInformation.firstName || '',
            middleInitial: identityInformation.secondName || '',
            lastName: identityInformation.paternalLastName || '',
            maternalLastName: identityInformation.maternalLastName || '',
            gender: identityInformation?.gender ? identityInformation?.gender.toUpperCase() : '',
            dob: identityInformation.dateOfBirth || '',
            maritalStatus: identityInformation.maritalStatus || '',
            phoneNumber: identityInformation.phoneNumber ? identityInformation.phoneNumber.replace(/\D/g, '') : '',
            countryCode: canEnableCountryCode?.paramValue === '1' ? identityInformation.countryCode : undefined,
            emailId: identityInformation.emailId || '',
            referralSources: identityInformation.racPromotions || '',
            phoneType: 'CELL',
            callTime: 'MORN',
            submittedDate: new Date().toISOString().split('T')[0],
            storeNumber: window.sessionStorage.getItem('storeNumber'),
            productCode: sessionStorageValues?.productCode || getStgCustomerRes?.productCode || undefined,
            customerCreatedSource: sessionStorageValues?.clientId || getStgCustomerRes?.customerSource || undefined,
            consentDate: getStgCustomerRes?.consentDate || null,
            customerId: sessionStorageValues?.type === 'EC' ? sessionStorageValues?.identifier : undefined,
            identityInfo: {
              curpId: identityInformation.curp,
              voterId: identityInformation.ineVoterid,
              rfcId: identityInformation.rfc,
              transactionGuid: latestIdentityRecord && latestIdentityRecord?.transactionGuid ? latestIdentityRecord?.transactionGuid : null,
              transactionDate: new Date().toISOString(),
              livenessChecked: 1,
              stgCustomerIdentityId: latestIdentityRecord && latestIdentityRecord?.stageCustomerIdentityId ? String(latestIdentityRecord?.stageCustomerIdentityId) : undefined,
              identityVerificationStatus: latestIdentityRecord?.identityVerification === "APPROVED" && Number(stgCustomerProfileId) ? 'APPROVED' : 'PENDING',
              idValidationType: 'NAAT',
              selfieValidationType: idDocType === 'SELF' ? 'NAAT' : null,
              // Stamp the agreementId so the backend can link this liveness check to
              // the specific rental agreement that initiated the DOF selfie flow.
              ...(idDocType === 'SELF' && sessionStorageValues?.agreementId
                ? { agreementId: sessionStorageValues.agreementId }
                : {}),
            },
          };
          console.log('Payload:', stgCustomerPayload);
          const isStoreCodeFlow = window.sessionStorage.getItem('authKey')?.length ? false : true;
          console.log('isStoreCodeFlow ', isStoreCodeFlow);
          const accessToken = isStoreCodeFlow ? window.sessionStorage.getItem('storeNumber') : window.sessionStorage.getItem('authKey');
          await CreateCustomerStg(stgCustomerPayload, isStoreCodeFlow, accessToken)
          setLiveLinessCompleted(true);
          history.push('/submission');
        }
        else {
          // No face image in result — route through shared failure handler
          console.log('[READ_SELFIE] No documentFrontData — delegating to handleSelfieFailure');
          onSelfieFailure(t('Selfie capture failed. Please try again.'));
        }
      }
      else {
        // No images in result — route through shared failure handler
        console.log('[READ_SELFIE] No apiResult.images — delegating to handleSelfieFailure');
        onSelfieFailure(t('Selfie capture failed. Please try again.'));
      }
    } catch (e: unknown) {
      // Guard against unhandled rejections from uploadDocuments/CreateCustomerStg —
      // this runs inside an SDK callback, so an uncaught throw here would otherwise
      // leave the loader stuck forever and surface as an unhandled promise rejection.
      console.error('[READ_SELFIE] Unexpected error while processing selfie verification result:', e);
      onSelfieFailure(t('Selfie capture failed. Please try again.'));
    } finally {
      setScreenLoader(false)
    }
  }

  return { readSdkResults, readSelfieVerificationData };
};
